/**
 * Pagina principal de chat direto com LLM (M8 / playground).
 *
 * Layout estilo Claude.ai:
 *  - Sidebar interno a esquerda (lista de threads ``type=chat``).
 *  - Area central com ChatStream + composer; ``ModelSelector`` fica
 *    EMBUTIDO no composer via prop ``composerLeftSlot`` (a esquerda
 *    do botao Enviar). Sem header dedicado.
 *  - Side panel direito (metricas + artefatos) reusando ``ChatSidePanel``.
 *
 * Cancelamos o padding do AppShell em ``/chat`` via margens negativas
 * para que a sidebar interna encoste no sidebar global do app -- sem
 * a borda escura indesejada entre as duas.
 *
 * Em ``/chat`` (sem id): user escolhe modelo + escreve primeira msg
 * -> criamos thread type=chat + navegamos pra ``/chat/{id}`` com a
 * mensagem como ``state.firstMessage`` -> ChatStream auto-envia ao
 * montar (prop ``autoSubmit``).
 */
import { Send } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/feedback/ErrorState";
import { Textarea } from "@/components/ui/textarea";
import { ChatHistorySidebar } from "@/features/chat/components/ChatHistorySidebar";
import { ModelSelector } from "@/features/chat/components/ModelSelector";
import { useAllowedModels } from "@/features/chat/hooks/useAllowedModels";
import {
  useCreateChatThread,
  useUpdateChatThreadModel,
} from "@/features/chat/hooks/useChatThreads";
import { ChatSidePanel } from "@/features/threads/components/ChatSidePanel";
import { ChatStream } from "@/features/threads/components/ChatStream";
import { useThread } from "@/features/threads/hooks/useThreads";
import { useUiStore } from "@/stores/uiStore";

export function DirectChatPage() {
  const { threadId } = useParams<{ threadId?: string }>();
  const location = useLocation();

  const models = useAllowedModels();
  const thread = useThread(threadId ?? "");

  // Default model auto-seleciona ao carregar lista.
  const defaultModelId = useMemo(() => {
    const list = models.data ?? [];
    const def = list.find((m) => m.is_default);
    return (def ?? list[0])?.id ?? "";
  }, [models.data]);

  // Estado local do modelo selecionado. Em /chat (sem id): inicia
  // com default. Em /chat/:id: sincroniza com thread.llm_model_id
  // **apenas na primeira carga** -- depois disso o estado local manda
  // (senao o useEffect derrubaria a escolha do user antes do PATCH
  // chegar e atualizar o cache do thread).
  const [selectedModelId, setSelectedModelId] = useState<string>("");
  const syncedForThreadId = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Reseta o ref quando o threadId muda -- proxima carga ressincroniza.
    if (syncedForThreadId.current !== threadId) {
      syncedForThreadId.current = undefined;
    }
    if (
      threadId &&
      thread.data?.llm_model_id &&
      syncedForThreadId.current !== threadId
    ) {
      setSelectedModelId(thread.data.llm_model_id);
      syncedForThreadId.current = threadId;
    } else if (!threadId && defaultModelId && !selectedModelId) {
      setSelectedModelId(defaultModelId);
    }
  }, [threadId, thread.data?.llm_model_id, defaultModelId, selectedModelId]);

  // ``firstMessage`` vem via state.navigate quando o user envia a
  // primeira msg na tela vazia. Capturamos via useEffect porque o
  // DirectChatPage permanece montado durante /chat -> /chat/:id, e
  // ``useState(() => ...)`` no mount NAO captura o state que so chega
  // no proximo render apos o navigate.
  //
  // AMARRADO ao threadId: o autoSubmit so vale para a thread que
  // acabou de ser criada. Sem isso, ao navegar para OUTRA thread no
  // historico, o valor antigo ("oi" da thread A) reenviaria na thread B.
  const [autoSubmit, setAutoSubmit] = useState<
    { threadId: string; message: string } | null
  >(null);
  useEffect(() => {
    const s = location.state as { firstMessage?: string } | null;
    const msg =
      typeof s?.firstMessage === "string" ? s.firstMessage : undefined;
    if (!msg || !threadId) return;
    setAutoSubmit({ threadId, message: msg });
    // Limpa state para que F5/voltar nao reenvie.
    window.history.replaceState({}, "");
  }, [location.state, threadId]);
  // So passa o autoSubmit se for da thread atual (evita vazar para
  // outra conversa ao trocar no historico).
  const autoSubmitOnce =
    autoSubmit && autoSubmit.threadId === threadId
      ? autoSubmit.message
      : undefined;

  if (threadId && thread.isError) {
    return <ErrorState error={thread.error} />;
  }

  return (
    <div className="flex h-full min-h-0">
      <ChatHistorySidebar activeThreadId={threadId} />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {threadId ? (
          // ``key={threadId}``: remonta limpo ao trocar de conversa no
          // historico. Sem isso, o ChatStream reusa a mesma instancia
          // (mesma rota /chat/:threadId) e os ``liveBlocks`` da conversa
          // anterior ficam sobrepostos ao historico da nova = duplicacao
          // visivel + balao sem badge de modelo.
          <ChatWithSidePanel
            key={threadId}
            threadId={threadId}
            autoSubmit={autoSubmitOnce}
            selectedModelId={selectedModelId}
            onChangeModel={setSelectedModelId}
          />
        ) : (
          <EmptyState
            selectedModelId={selectedModelId}
            onChangeModel={setSelectedModelId}
          />
        )}
      </div>
    </div>
  );
}

function ChatWithSidePanel({
  threadId,
  autoSubmit,
  selectedModelId,
  onChangeModel,
}: {
  threadId: string;
  autoSubmit?: string;
  selectedModelId: string;
  onChangeModel: (id: string) => void;
}) {
  const { t } = useTranslation("directChat");
  const collapsed = useUiStore((s) => s.chatSidePanelCollapsed);
  const toggle = useUiStore((s) => s.toggleChatSidePanel);
  const updateModel = useUpdateChatThreadModel(threadId);

  // Refresh metrics + artefatos quando o stream termina.
  const [refreshKey, setRefreshKey] = useState(0);
  const [streaming, setStreaming] = useState(false);

  async function handleChange(newId: string) {
    onChangeModel(newId);
    try {
      await updateModel.mutateAsync(newId);
    } catch (err) {
      toastApiError(err);
      toast.error(t("errors.modelChangeFailed"));
    }
  }

  return (
    <div className="flex min-h-0 flex-1 gap-3 overflow-hidden px-3 pt-3">
      {/* Miolo do chat: mensagens + composer (com ModelSelector +
          Enviar). ``pb-3`` da o respiro inferior para o composer nao
          encostar na borda da viewport. O wrapper externo tem o respiro
          lateral/superior; a borda externa (encostar no sidebar global)
          continua sem padding por causa do route ``flush``. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col pb-3">
        <ChatStream
          threadId={threadId}
          autoSubmit={autoSubmit}
          composerLeftSlot={
            <ModelSelector
              value={selectedModelId}
              onChange={handleChange}
              disabled={updateModel.isPending}
            />
          }
          onSseEvent={(ev) => {
            if (ev.event === "agent_started") setStreaming(true);
            if (
              ev.event === "done" ||
              ev.event === "agent_completed" ||
              ev.event === "error"
            ) {
              setStreaming(false);
              setRefreshKey((k) => k + 1);
            }
          }}
        />
      </div>
      <ChatSidePanel
        threadId={threadId}
        isTeamSession={false}
        streaming={streaming}
        refreshKey={refreshKey}
        collapsed={collapsed}
        onToggleCollapsed={toggle}
        className={collapsed ? "mb-3 w-12 shrink-0" : "mb-3 w-80 shrink-0"}
      />
    </div>
  );
}

/** Tela vazia (sem thread): mensagem central + composer com seletor de
 * modelo a esquerda do botao Enviar. */
function EmptyState({
  selectedModelId,
  onChangeModel,
}: {
  selectedModelId: string;
  onChangeModel: (id: string) => void;
}) {
  const { t } = useTranslation("directChat");
  const navigate = useNavigate();
  const createMut = useCreateChatThread();
  const [text, setText] = useState("");

  async function handleSend() {
    if (!selectedModelId) {
      toast.error(t("errors.noModelSelected"));
      return;
    }
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      const created = await createMut.mutateAsync({
        llm_model_id: selectedModelId,
      });
      navigate(`/chat/${created.id}`, {
        state: { firstMessage: trimmed },
      });
    } catch (err) {
      toastApiError(err);
    }
  }

  const sending = createMut.isPending;
  return (
    <div className="flex min-h-0 flex-1 flex-col px-6">
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold">{t("title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("subtitle")}
          </p>
        </div>
      </div>
      <div className="mx-auto w-full max-w-2xl pb-3">
        <div className="rounded-lg border bg-card p-3">
          <Textarea
            rows={3}
            placeholder={t("composer.placeholder")}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
            className="min-h-0 resize-none border-0 shadow-none focus-visible:ring-0"
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <ModelSelector
              value={selectedModelId}
              onChange={onChangeModel}
              disabled={sending}
            />
            <Button
              size="sm"
              onClick={() => void handleSend()}
              disabled={
                sending || !selectedModelId || text.trim().length === 0
              }
            >
              <Send className="h-4 w-4" />
              {sending ? t("composer.sending") : t("composer.send")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
